import { useEffect, useRef, useState } from 'react';
import { FileUp, Loader2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/lib/errors';
import { DOCUMENT_ACCEPT } from './ui';
import { formatFileSize } from './documentsModel';
import { fileProblem } from './UploadDocumentPanel';

/**
 * "Upload new version" — one file + what changed. Saves as the next version of the same
 * document (same project, title and type); the current version stays in the history.
 * onUpload({ file, remarks }) must throw on failure (the error is shown in the dialog).
 * initialFile: a file dropped on the document's version history ("Drop a file to save as v3").
 */
export default function UploadVersionDialog({ open, onOpenChange, document: doc, nextVersion, onUpload, initialFile = null }) {
  const [file, setFile] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  const remarksRef = useRef(null);
  const browseRef = useRef(null);

  // A chosen or dropped file replaces the "browse" button that had focus: focus moves on to "What changed?"
  // (or back to "browse" when the file is removed) instead of falling out of the form.
  const chooseFile = (next) => {
    setFile(next);
    window.setTimeout(() => (next ? remarksRef.current : browseRef.current)?.focus({ preventScroll: true }), 0);
  };

  useEffect(() => {
    if (open) {
      setFile(initialFile || null);
      setRemarks('');
      setError('');
      setBusy(false);
    }
  }, [open, initialFile]);

  const submit = async (event) => {
    event.preventDefault();
    if (!file || busy) return;
    const problem = fileProblem(file);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError('');
    try {
      await onUpload({ file, remarks: remarks.trim() });
      onOpenChange(false);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not upload the file'));
    } finally {
      setBusy(false);
    }
  };

  // The whole dialog takes a dropped file (not only the dashed box), and its drag events stop here: the
  // Documents page behind (React events bubble out of portals) must not open its Upload drawer as well.
  const dragHandlers = {
    onDragEnter: (event) => { event.preventDefault(); event.stopPropagation(); setDragging(true); },
    onDragOver: (event) => { event.preventDefault(); event.stopPropagation(); setDragging(true); },
    onDragLeave: (event) => {
      event.stopPropagation();
      if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false);
    },
    onDrop: (event) => {
      event.preventDefault();
      event.stopPropagation();
      setDragging(false);
      const dropped = event.dataTransfer?.files?.[0];
      if (dropped && !busy) chooseFile(dropped);
    },
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!busy) onOpenChange(next); }}>
      <DialogContent
        className="max-w-[460px] gap-0 rounded-card border-line bg-surface p-0 text-ink shadow-overlay"
        data-testid="upload-version-dialog"
        onOpenAutoFocus={(event) => {
          // Opened with a dropped file: the next thing to fill in is "What changed?".
          if (!initialFile) return;
          event.preventDefault();
          remarksRef.current?.focus({ preventScroll: true });
        }}
        {...dragHandlers}
      >
        <form onSubmit={submit}>
          <div className="border-b border-line-2 px-6 pb-4 pt-5">
            <DialogTitle className="pr-8 text-[16px] font-bold leading-snug text-ink">
              Upload new version
            </DialogTitle>
            <DialogDescription className="mt-1 text-[13px] text-ink-3">
              Saves <span className="font-mono font-semibold text-ink-2">v{nextVersion}</span> of “{doc?.title}”.
              {' '}<span className="font-mono">v{doc?.version || 1}</span> stays in the version history.
            </DialogDescription>
          </div>
          <div className="space-y-4 px-6 py-5">
            <div
              data-testid="upload-version-dropzone"
              className={cn(
                'flex flex-col items-center gap-1.5 rounded-[10px] border-[1.5px] border-dashed px-4 py-5 text-center text-[12.5px] text-ink-3 transition-colors',
                dragging ? 'border-[color:var(--brand-ink)] bg-brand-soft' : 'border-line-2'
              )}
            >
              {file ? (
                <div className="flex w-full items-center gap-2.5 text-left">
                  <FileUp className="h-4 w-4 shrink-0 text-brand-ink" aria-hidden="true" />
                  <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-ink">{file.name}</span>
                  <span className="shrink-0 text-ink-3">{formatFileSize(file.size)}</span>
                  <button
                    type="button"
                    onClick={() => chooseFile(null)}
                    className="grid h-6 w-6 shrink-0 place-items-center rounded text-ink-3 hover:bg-surface-hover hover:text-ink"
                    aria-label="Remove file"
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </div>
              ) : (
                <>
                  <FileUp className="h-5 w-5 text-ink-3" aria-hidden="true" />
                  <span>
                    Drop a file here or{' '}
                    <button ref={browseRef} type="button" className="font-semibold text-brand-ink hover:underline" onClick={() => inputRef.current?.click()}>
                      browse
                    </button>
                  </span>
                  <span className="text-[11.5px]">PDF, Word, Excel, CSV or images</span>
                </>
              )}
              <input
                ref={inputRef}
                type="file"
                accept={DOCUMENT_ACCEPT}
                className="sr-only"
                tabIndex={-1}
                data-testid="upload-version-file"
                onChange={(event) => { const picked = event.target.files?.[0]; if (picked) chooseFile(picked); event.target.value = ''; }}
              />
            </div>
            <label className="block">
              <span className="mb-1.5 block text-[12.5px] font-semibold text-ink-2">What changed? <span className="font-normal text-ink-3">(optional)</span></span>
              <input
                ref={remarksRef}
                type="text"
                value={remarks}
                maxLength={300}
                onChange={(event) => setRemarks(event.target.value)}
                placeholder="e.g. Updated per consultant comments"
                className="h-9 w-full rounded-control border border-line bg-surface px-3 text-[13.5px] text-ink outline-none placeholder:text-ink-4 focus:border-[color:var(--brand-ink)] focus:ring-2 focus:ring-[color:var(--brand-soft-border)]"
                data-testid="upload-version-remarks"
              />
            </label>
            {error ? (
              <p role="alert" className="rounded-lg bg-status-red-bg px-3 py-2 text-[12.5px] font-medium text-status-red-ink">{error}</p>
            ) : null}
          </div>
          <div className="flex justify-end gap-2 border-t border-line-2 px-6 py-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
            <Button type="submit" disabled={!file || busy} data-testid="upload-version-submit">
              {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : <FileUp aria-hidden="true" />}
              Save as v{nextVersion}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
